import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const [result, setResult] = useState(null);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setResult(data);
          setState("success");
          if (refresh) await refresh();
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
  }, [refresh]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center animate-fade-in-up" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-[#C86B5E] animate-spin mx-auto mb-8" />
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mb-4">
            Vérification du paiement…
          </h1>
          <p className="text-[#5C5754] text-lg">Merci de patienter quelques secondes.</p>
        </>
      )}

      {state === "success" && (
        <>
          <div className="w-16 h-16 rounded-full bg-[#7A8B76]/15 flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-9 h-9 text-[#7A8B76]" strokeWidth={1.5} />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
            Paiement confirmé
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight mb-6">
            {result?.item_type === "pack"
              ? "Le pack complet est débloqué !"
              : "L'analyse est débloquée !"}
          </h1>
          <p className="text-[#5C5754] text-lg leading-relaxed mb-12">
            Merci pour votre confiance. Vous pouvez désormais générer{" "}
            {result?.item_type === "pack" ? "toutes vos analyses" : "votre analyse"} détaillées.
          </p>
          {result?.item_type === "module" && result?.module_id ? (
            <button
              data-testid="payment-goto-module"
              onClick={() => nav(`/module/${result.module_id}`)}
              className="bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-full px-10 py-4 transition-colors inline-flex items-center gap-3"
            >
              Générer mon analyse <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <Link
              to="/dashboard"
              data-testid="payment-goto-dashboard"
              className="bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-full px-10 py-4 transition-colors inline-flex items-center gap-3"
            >
              Retour au parcours <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </>
      )}

      {(state === "error" || state === "timeout") && (
        <>
          <div className="w-16 h-16 rounded-full bg-[#C86B5E]/15 flex items-center justify-center mx-auto mb-8">
            <XCircle className="w-9 h-9 text-[#C86B5E]" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mb-4">
            {state === "timeout" ? "Paiement en cours de traitement" : "Un souci est survenu"}
          </h1>
          <p className="text-[#5C5754] text-lg leading-relaxed mb-12">
            {state === "timeout"
              ? "Votre paiement met plus de temps que prévu. Il sera confirmé automatiquement. Revenez sur votre parcours dans un instant."
              : "Nous n'avons pas pu confirmer votre paiement. Aucun montant n'a été débité si le paiement n'a pas abouti."}
          </p>
          <Link
            to="/dashboard"
            data-testid="payment-error-back"
            className="text-[#C86B5E] hover:text-[#B35A4E] inline-flex items-center gap-2"
          >
            Retour au parcours
          </Link>
        </>
      )}
    </div>
  );
}
